import React, { useState, useEffect, useRef, Fragment } from "react";
import { UserAccess } from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import { Button } from "components/buttons";
import { FormTextInput } from "components/form-fields";
import { ListingTable } from "components/listing";
import { Tooltip } from "components/tooltip";
import { ShowHide } from "components/layout";
import { NotificationToaster } from "components/notifications";
import { Intent } from "@blueprintjs/core";
import { useDebounce } from "hooks/useDebounce";
import { PcnService } from "services/PcnService";
import { PcnReasonForContraventionModal } from "pages/pcn/admin/settings/PcnReasonForContraventionModal";

const requiredAction = "Internal";
const pageSize = 50;
const debounceTimeout = 750;
const badRequestCode = 400;
const generalErrorMessage = "An error occurred while loading contravention reasons. Please try again.";

const defaultModel = {
    id: 0,
    contraventionReason: "",
    timeRequired: false
};

const sortableHeaders = [
    { headerName: "Contravention Reason", sortName: "CONTRAVENTIONREASON" },
    { headerName: "Time Required", sortName: "TIMEREQUIRED" }
];

const tableHeaders = ["Contravention Reason", "Time Required", "Actions"];

export function PcnReasonsForContraventionsTab() {
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);
    const [loading, setLoading] = useState(true);
    const [tableRows, setTableRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [sortedBy, setSortedBy] = useState(sortableHeaders[0]);
    const [sortedDir, setSortedDir] = useState("A");

    const [showModal, setShowModal] = useState(false);
    const [isModalUpdate, setIsModalUpdate] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedReason, setSelectedReason] = useState(defaultModel);

    const inputSearchRef = useRef(null);

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
        setPageNumber(1);
    }

    function onSort(header, direction) {
        setSortedBy(header);
        setSortedDir(direction);
        setPageNumber(1);
    }

    function onPagingChange(newPageNumber) {
        setPageNumber(newPageNumber);
    }

    function addContraventionReason() {
        setIsModalUpdate(false);
        setSelectedReason(defaultModel);
        setShowModal(true);
    }

    function onEditContraventionReason(id) {
        setModalLoading(true);
        setIsModalUpdate(true);
        setShowModal(true);

        PcnService.getContraventionReason(id)
            .then((details) => {
                setSelectedReason(details);
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Could not load the contravention reason. Please try again.");
                onCloseModal();
            })
            .finally(() => {
                setModalLoading(false);
            });
    }

    function onCloseModal() {
        setShowModal(false);
        setSelectedReason(defaultModel);
        setIsModalUpdate(false);
    }

    function onSaveModal() {
        onCloseModal();
        fetchData();
    }

    function fetchData() {
        setLoading(true);

        const searchRequest = {
            requestCount: pageSize,
            pageNumber: pageNumber,
            searchTerm: debouncedSearchTerm,
            sortField: sortedBy.sortName,
            sortDirection: sortedDir
        };

        PcnService.searchContraventionReasons(searchRequest)
            .then((response) => {
                const rows = (response.data ?? []).map((row) => {
                    return [
                        row.contraventionReason,
                        row.timeRequired ? "Yes" : "No",
                        <div key={`${row.id}-actions`} className="button-row-small inline-items">
                            <Tooltip content="Edit contravention reason">
                                <Button
                                    onClick={() => onEditContraventionReason(row.id)}
                                    icon="edit"
                                    iconOnly={true}
                                    minimal={true}
                                    large={false}
                                />
                            </Tooltip>
                        </div>
                    ];
                });

                setTableRows(rows);
                setTotalRecords(response.totalCount);
            }, (error) => {
                if (error?.response?.status === badRequestCode) {
                    NotificationToaster.show(Intent.WARNING, error.response.data);
                } else {
                    NotificationToaster.show(Intent.DANGER, generalErrorMessage);
                }
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, generalErrorMessage);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    useEffect(() => {
        fetchData();
    }, [debouncedSearchTerm, pageNumber, sortedBy, sortedDir]);

    return (
        <UserAccess
            perform={requiredAction}
            yes={() => (
                <Fragment>
                    <div className="spacer-bottom">
                        <Button intent="primary" text="Add new contravention reason" id="add-contravention-reason" onClick={addContraventionReason} loading={loading} />
                    </div>

                    <FormTextInput
                        inputRef={inputSearchRef}
                        placeholder="Search contravention reasons"
                        disabled={loading && inputSearchRef.current !== document?.activeElement}
                        onChange={onSearchChange}
                        value={searchTerm}
                        large
                        icon="search"
                        id="contravention-reason-search-field"
                        fill
                    />

                    <ListingTable
                        id="listing-table-contravention-reasons"
                        headers={tableHeaders}
                        data={tableRows}
                        totalRecordCount={totalRecords}
                        loadingData={loading}
                        onPagingChange={onPagingChange}
                        startPage={pageNumber}
                        pageSize={pageSize}
                        showPerPage={false}
                        sortable
                        sortableHeaders={sortableHeaders}
                        sortedBy={sortedBy}
                        sortedDir={sortedDir}
                        onSort={onSort}
                        noDataMessage="No contravention reasons to display"
                    />

                    <ShowHide
                        evaluator={showModal}
                        show={(
                            <PcnReasonForContraventionModal
                                showModal={showModal}
                                modalTitle={isModalUpdate ? "Update Contravention Reason" : "Add New Contravention Reason"}
                                isUpdate={isModalUpdate}
                                loading={modalLoading}
                                onSave={onSaveModal}
                                onCloseModal={onCloseModal}
                                contraventionReasonDetails={selectedReason}
                            />
                        )}
                    />
                </Fragment>
            )}
            no={() => (
                <div className="row">
                    <PageRestricted />
                </div>
            )}
        />
    );
}
